import React, { useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "../home/shared";
import { QuotePlaceholders } from "./Sections";
import { ResearchPanel } from "./ResearchPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD = [
  { n: "1", title: "Regulate", lead: "Ground the nervous system.", copy: "We start by slowing everything down, through breath, stillness and time in nature, creating the conditions to reconnect with yourself.", image: "/home/breathwork.webp" },
  { n: "2", title: "Reconnect", lead: "Return to yourself, to others and to the natural world.", copy: "Through shared experiences, honest conversation and time outdoors, we rebuild the connections modern life pulls apart.", image: "/home/circle.webp" },
  { n: "3", title: "Rewild", lead: "Move, breathe, feast, gather.", copy: "This is where the energy lifts, through movement, food, the sea, the fire and the people around you. Remember who you are.", image: "/home/fire-feast.webp" },
  { n: "4", title: "Rise", lead: "Take what you've learned and move forward with clarity and purpose.", copy: "We help you carry it home, so the change doesn't stop when the weekend does.", image: "/home/anglesey-sea.webp" },
];

const GALLERY = ["/home/candle-dinner.webp", "/spring-soundbath.jpg", "/retreat-group.webp", "/home/laughing.webp", "/spring-talk.jpg", "/retreat-food.webp", "/home/hottub.webp"];

export const FestSignup = () => {
  const [f, setF] = useState({ name: "", email: "" });
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !/^\S+@\S+\.\S+$/.test(f.email)) { setState("invalid"); return; }
    setState("sending");
    try { await axios.post(`${API}/newsletter`, { ...f, source: "feral-fest-coming-soon" }); setState("done"); } catch { setState("error"); }
  };
  return (
    <section className="relative bg-[#2c6052] text-[#f2e5de] overflow-hidden" data-testid="events-fest">
      <img src="/spring-fest-lawn.jpg" alt="FERAL Fest" className="absolute inset-0 w-full h-full object-cover opacity-45" loading="lazy" />
      <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 py-24 md:py-36 grid md:grid-cols-12 gap-10 items-end">
        <Reveal className="md:col-span-6">
          <Eyebrow className="text-[#f2e5de]/80 mb-5">Coming soon</Eyebrow>
          <h2 className="feral-serif text-[#ebc754] text-6xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">FERAL Fest</h2>
          <p className="mt-6 text-lg font-light text-[#f2e5de]/85">Coming soon. Sign up to be the first to hear.</p>
        </Reveal>
        <Reveal className="md:col-span-6" delay={0.1}>
          {state === "done" ? (
            <p className="flex items-center gap-3 text-[#9b3d0a] text-lg" data-testid="fest-signup-success"><Check size={20} /> You're on the list.</p>
          ) : (
            <form noValidate onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="fest-signup-form">
              <input aria-label="Name" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="fest-signup-name" className="flex-1 bg-transparent border-b border-[#f2e5de]/40 focus:border-[#ebc754] outline-none py-4 placeholder:text-[#f2e5de]/50" />
              <input aria-label="Email" type="email" placeholder="Your email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="fest-signup-email" className="flex-1 bg-transparent border-b border-[#f2e5de]/40 focus:border-[#ebc754] outline-none py-4 placeholder:text-[#f2e5de]/50" />
              <button disabled={state === "sending"} data-testid="fest-signup-submit" className="feral-header text-xs font-bold tracking-[0.2em] bg-[#ebc754] text-[#231f20] px-7 py-4 hover:bg-[#f2e5de] transition-colors">{state === "sending" ? "Sending…" : "Keep me posted"}</button>
            </form>
          )}
          {state === "invalid" && <p className="mt-3 text-sm text-[#f0a868]" data-testid="fest-signup-error">Please add your name and a valid email.</p>}
          {state === "error" && <p className="mt-3 text-sm text-[#f0a868]" data-testid="fest-signup-error">Something went wrong — please try again.</p>}
        </Reveal>
      </div>
    </section>
  );
};

export const MethodSection = () => (
  <section id="feral-method" className="feral-canvas py-24 md:py-36 px-6 md:px-10 scroll-mt-20" data-testid="events-method">
    <div className="max-w-[1500px] mx-auto">
      <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-24">
        <Reveal className="md:col-span-6"><Eyebrow className="text-[#9b3d0a] mb-5">The FERAL Method</Eyebrow><h2 className="feral-serif text-[#9b3d0a] text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">Four stages. Informed by research.</h2></Reveal>
        <Reveal className="md:col-span-5 md:col-start-8 self-end" delay={0.1}><p className="text-lg font-light leading-relaxed text-[#231f20]/80">Every FERAL event moves through the same four-stage cycle, woven through every session and every gathering. So the time you spend with us has purpose, and you leave with something that lasts.</p></Reveal>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-[#2c6052]/15">
        {METHOD.map((m, i) => (
          <Reveal key={m.title} delay={i * 0.1} className={`pt-8 pb-4 lg:px-6 ${i ? "lg:border-l border-[#2c6052]/15" : ""}`}>
            <div data-testid={`method-stage-${m.title.toLowerCase()}`}>
              <div className="flex items-baseline gap-4"><span className="feral-serif text-[#2c6052] text-5xl">{m.n}</span><h3 className="feral-serif text-[#9b3d0a] text-4xl">{m.title}</h3></div>
              <div className="mt-6 overflow-hidden aspect-[4/3]"><img src={m.image} alt={m.title} loading="lazy" className="w-full h-full object-cover" /></div>
              <p className="mt-6 font-semibold text-[#231f20]">{m.lead}</p>
              <p className="mt-2 font-light leading-relaxed text-[#231f20]/75">{m.copy}</p>
              {i < 3 && <p className="hidden lg:block mt-6 text-[#9b3d0a] feral-serif text-2xl">→</p>}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ScienceSection = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-[#2c6052] feral-texture-dark text-[#f2e5de] py-24 md:py-32 px-6 md:px-10" data-testid="events-science">
      <div className="max-w-[1500px] mx-auto">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-6">
            <Eyebrow className="text-[#f2e5de]/70 mb-5">The science</Eyebrow>
            <h2 className="feral-serif text-[#ebc754] text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight" data-testid="science-heading">Rooted in nature. Informed by research.</h2>
          </Reveal>
          <Reveal className="md:col-span-5 md:col-start-8" delay={0.1}>
            <p className="text-lg md:text-xl font-light leading-relaxed text-[#f2e5de]/90" data-testid="science-copy">Breathwork. Movement. Time outdoors. Human connection. The FERAL Method brings these together with reflection and practical steps forward, drawing on research into wellbeing and behaviour change.</p>
            <p className="mt-8 flex items-baseline gap-3" data-testid="science-count"><span className="feral-serif text-[#ebc754] text-5xl md:text-6xl leading-none">22</span><span className="feral-serif text-[#ebc754] text-xl md:text-2xl">peer-reviewed research papers</span></p>
            <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="research-panel" data-testid="science-toggle"
              className="mt-8 inline-flex items-center feral-nav uppercase tracking-[0.06em] text-sm px-6 h-[52px] border border-[#ebc754] text-[#ebc754] hover:bg-[#ebc754] hover:text-[#9b3d0a] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#f2e5de]">
              {open ? "Close the research −" : "Explore the research +"}
            </button>
          </Reveal>
        </div>
        <ResearchPanel open={open} />
      </div>
    </section>
  );
};

export const GallerySection = () => (
  <section className="feral-canvas py-24 md:py-32" data-testid="events-gallery">
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 mb-12"><Reveal><h2 className="feral-serif text-[#9b3d0a] text-5xl sm:text-6xl leading-[0.95] tracking-tight">Gallery.</h2></Reveal></div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-2">
      <div className="relative col-span-2 row-span-2 aspect-square md:aspect-auto overflow-hidden">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline preload="metadata" poster="/retreat-food.webp">
          <source src="/retreat-lunch.webm" type="video/webm" />
          <source src="/retreat-lunch.mp4" type="video/mp4" />
        </video>
      </div>
      {GALLERY.slice(0, 6).map((src) => (
        <div key={src} className="relative aspect-square overflow-hidden"><img src={src} alt="A FERAL gathering" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-105" /></div>
      ))}
    </div>
  </section>
);

export const Reviews = () => <QuotePlaceholders heading="In their words." labels={["Past guest", "Past guest", "Past guest"]} testid="events-reviews" />;
